import { transformCatalogueEditions } from '#lib/websites/artists/pages/ArtistDetailPage/utils/transformCatalogueEditions/index.js';
import { transformCataloguePrimaryArtworks } from '#lib/websites/artists/pages/ArtistDetailPage/utils/transformCataloguePrimaryArtworks/index.js';
import type { ArtistsPageData } from '$routes/artists/[slug]/types';

export type ArtistSectionKey =
	| 'editions'
	| 'primary'
	| 'merch'
	| 'books'
	| 'nfts'
	| 'talks'
	| 'podcasts'
	| 'exhibitions';

export interface ArtistSection {
	key: ArtistSectionKey;
	label: string;
	count: number;
}

interface GetArtistSectionsArgs {
	artist: ArtistsPageData['artist'] | undefined;
	catalogue: ArtistsPageData['catalogue'] | undefined;
	booksResponse: ArtistsPageData['booksResponse'] | undefined;
}

interface ArtistSectionCandidate {
	key: ArtistSectionKey;
	label: string;
	items: readonly unknown[] | null | undefined;
	total: number | null | undefined;
}

/**
 * The artist page sections that actually have content, in display order.
 *
 * Counts prefer the aggregated totals (the catalogue aggregates, the `*_func`
 * counts on the artist for the curated relations) and fall back to the loaded
 * array length when no aggregate is available.
 *
 * Editions and Primary are the exception: they show one card per series or
 * primary collection, so they count the cards actually rendered rather than the
 * individual works those groups contain.
 */
export const getArtistSections = ({
	artist,
	catalogue,
	booksResponse,
}: GetArtistSectionsArgs): ArtistSection[] => {
	const editionCards = transformCatalogueEditions(
		catalogue?.series,
		catalogue?.serieslessEditions
	);
	const primaryCards = transformCataloguePrimaryArtworks(
		catalogue?.primaryCollections
	);

	const candidates: ArtistSectionCandidate[] = [
		{
			key: 'editions',
			label: 'Editions',
			items: editionCards,
			total: editionCards.length,
		},
		{
			key: 'primary',
			label: 'Primary',
			items: primaryCards,
			total: primaryCards.length,
		},
		{
			key: 'merch',
			label: 'Merch',
			items: catalogue?.merchItems,
			total: catalogue?.merchItemsCount,
		},
		{
			key: 'books',
			label: 'Books',
			items: booksResponse?.Publishing_Books,
			total: booksResponse?.Publishing_Books_aggregated[0]?.countDistinct?.id,
		},
		{
			key: 'nfts',
			label: 'NFTs',
			items: artist?.nft_collections,
			total: artist?.totalNfts?.count,
		},
		{
			key: 'talks',
			label: 'Talks',
			items: artist?.talks,
			total: artist?.totalTalks?.count,
		},
		{
			key: 'podcasts',
			label: 'Podcasts',
			items: artist?.podcast_episodes,
			total: artist?.totalPodcasts?.count,
		},
		{
			key: 'exhibitions',
			label: 'Exhibitions',
			items: artist?.exhibitions,
			total: artist?.totalExhibitions?.count,
		},
	];

	return candidates
		.filter((candidate) => (candidate.items?.length ?? 0) > 0)
		.map(({ key, label, items, total }) => ({
			key,
			label,
			count: total || items?.length || 0,
		}));
};
